import { AlertTriangle, ExternalLink, Link2, Check, RotateCw, CornerDownRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import FeedbackButtons from "@/components/FeedbackButtons";
import RerunModal from "@/components/RerunModal";

export default function UnlockedView({
  questions,
  topAnswers,
  caseId,
  meta,
  problemType,
  clarifyingQuestions,
  currentAnswers,
  parentCaseId,
  onRerunStarted,
}) {
  const [copied, setCopied] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const shareUrl =
    caseId && typeof window !== "undefined"
      ? `${window.location.origin}/report/${caseId}`
      : "";

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };

  const answerByQ = new Map((topAnswers || []).map((a) => [a.question_id, a]));
  const top5 = questions.slice(0, 5);
  const rest = questions.slice(5);

  return (
    <div className="space-y-8" data-testid="unlocked-view">
      <div className="rounded-2xl border border-[#35D07F]/40 bg-[#12181A] p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
              Unlocked
            </div>
            <div className="mt-2 text-white text-lg">
              Full findings and conflict flags below. Bookmark this page — the
              URL is yours.
            </div>
            {meta?.display_name && (
              <div className="mt-1 text-xs text-[#A0AAB2]">
                {meta.display_name}
                {meta.created_at
                  ? ` · ${new Date(meta.created_at).toLocaleDateString()}`
                  : ""}
              </div>
            )}
            {parentCaseId && (
              <div className="mt-3">
                <Link
                  to={`/report/${parentCaseId}`}
                  data-testid="parent-case-link"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60 hover:text-[#35D07F] transition-colors"
                >
                  <CornerDownRight className="h-3 w-3" strokeWidth={2.5} />
                  Rerun of a previous case
                </Link>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {clarifyingQuestions && clarifyingQuestions.length > 0 && (
              <button
                onClick={() => setRerunOpen(true)}
                data-testid="rerun-open"
                className="inline-flex items-center gap-2 rounded-full border border-[#35D07F]/60 bg-[#35D07F]/10 hover:bg-[#35D07F]/20 text-[#35D07F] text-xs px-4 h-9 transition-colors font-semibold"
              >
                <RotateCw className="h-3.5 w-3.5" strokeWidth={2.5} />
                Tweak & rerun
              </button>
            )}
            {caseId && (
              <button
                onClick={copyLink}
                data-testid="copy-share-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0B0F10] hover:border-[#35D07F]/60 text-white text-xs px-4 h-9 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-[#35D07F]" strokeWidth={3} />
                    Copied
                  </>
                ) : (
                  <>
                    <Link2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Copy share link
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <RerunModal
        open={rerunOpen}
        onClose={() => setRerunOpen(false)}
        caseId={caseId}
        clarifyingQuestions={clarifyingQuestions || []}
        currentAnswers={currentAnswers || []}
        onSubmitted={(newCaseId) => {
          setRerunOpen(false);
          if (onRerunStarted) onRerunStarted(newCaseId);
        }}
      />

      <div className="space-y-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
          Top 5 · full answers
        </div>
        {top5.map((q, qi) => {
          const a = answerByQ.get(q.id);
          return (
            <div
              key={q.id}
              data-testid={`unlocked-answer-${qi}`}
              className="rounded-2xl border border-white/10 bg-[#12181A] p-6"
            >
              <div className="flex items-start gap-3">
                <span className="text-xs font-mono text-[#35D07F] pt-1">
                  {String(q.rank).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 text-white font-semibold leading-relaxed text-[15px] sm:text-base">
                      {q.text}
                    </div>
                    {caseId && (
                      <FeedbackButtons
                        caseId={caseId}
                        itemType="answer"
                        itemId={q.id}
                        itemText={q.text}
                        themeLabel={q.theme_label}
                        problemType={problemType}
                      />
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    {(a?.categories || []).map((c, ci) => (
                      <div
                        key={ci}
                        className="rounded-lg border border-white/10 bg-[#0B0F10] p-4"
                      >
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#35D07F]">
                          {c.label}
                        </div>
                        <div className="mt-2 text-sm text-white/90 leading-relaxed">
                          {c.finding}
                        </div>
                        {c.sources && c.sources.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {c.sources.map((s, si) => (
                              <a
                                key={si}
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] text-[#A0AAB2] hover:text-[#35D07F] transition-colors"
                              >
                                <ExternalLink className="h-3 w-3" strokeWidth={2} />
                                {s.publisher || s.title || s.url}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {(!a?.categories || a.categories.length === 0) && (
                      <div className="text-sm text-white/50">
                        No findings could be sourced for this question. Try
                        re-running with sharper clarifiers.
                      </div>
                    )}
                  </div>

                  {a?.conflicts && a.conflicts.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {a.conflicts.map((cf, ci) => (
                        <div
                          key={ci}
                          className="rounded-lg border border-[#FF5252]/40 bg-[#FF5252]/[0.06] p-3"
                          data-testid={`unlocked-conflict-${qi}-${ci}`}
                        >
                          <div className="flex items-center gap-2 text-[#FF5252] text-[11px] font-semibold uppercase tracking-[0.15em]">
                            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
                            Sources disagree
                          </div>
                          <div className="mt-1 text-[15px] text-white/95 font-medium leading-relaxed">
                            {cf.point}
                          </div>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px]">
                            <div className="rounded-md border border-white/10 bg-[#0B0F10] p-2">
                              <div className="text-white">{cf.value_a}</div>
                              {cf.source_a?.url && (
                                <a
                                  href={cf.source_a.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1 text-[10px] text-[#35D07F] inline-flex items-center gap-1"
                                >
                                  <ExternalLink className="h-2.5 w-2.5" />
                                  {cf.source_a.publisher || cf.source_a.title}
                                </a>
                              )}
                            </div>
                            <div className="rounded-md border border-white/10 bg-[#0B0F10] p-2">
                              <div className="text-white">{cf.value_b}</div>
                              {cf.source_b?.url && (
                                <a
                                  href={cf.source_b.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1 text-[10px] text-[#35D07F] inline-flex items-center gap-1"
                                >
                                  <ExternalLink className="h-2.5 w-2.5" />
                                  {cf.source_b.publisher || cf.source_b.title}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#12181A] p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            Also worth researching ({rest.length})
          </div>
          <ul className="mt-4 space-y-3">
            {rest.map((q) => (
              <li
                key={q.id}
                className="flex gap-3 text-[15px] text-white/85 leading-relaxed"
                data-testid={`extra-question-${q.rank}`}
              >
                <span className="text-[11px] font-mono text-white/30 pt-0.5 w-6 shrink-0">
                  {String(q.rank).padStart(2, "0")}
                </span>
                <div>
                  <div>{q.text}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-[#35D07F]">
                    {q.theme_label}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
